import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import {
  CheckCircle2, AlertTriangle, ChevronDown, Eye, ArrowRight,
  Phone, PhoneOff, Loader2, XCircle, BrainCircuit,
  RefreshCcw, Check, X, Trophy, Sparkles, TrendingUp, BookOpen, Target
} from 'lucide-react';
import { motion, AnimatePresence, MotionConfig } from 'framer-motion';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import { API_URLS } from '../config/api';
import { supabase } from '../supabaseClient';

import {
  LiveKitRoom, RoomAudioRenderer, useVoiceAssistant, useConnectionState
} from '@livekit/components-react';
import { ConnectionState } from 'livekit-client';

/* ============================================================
   CONSTANTES
   ============================================================ */
const EASE_OUT = [0.22, 1, 0.36, 1];
const CALL_MAX_SECONDS = 300; // durée max d'un appel
const GRADE_MODAL_MS = 7000; // durée d'affichage de la modale de note
const DOCK_BOTTOM = 'calc(max(env(safe-area-inset-bottom), 0.5rem) + 12px)'; // position des widgets flottants
const TOAST_BOTTOM = 'calc(max(env(safe-area-inset-bottom), 0.5rem) + 88px)';

const PUCK_IMG = 'https://res.cloudinary.com/do5zemcrk/image/upload/v1768825506/Gemini_Generated_Image_a2m7m8a2m7m8a2m7_qedpsx.png';
const KORE_IMG = 'https://res.cloudinary.com/do5zemcrk/image/upload/v1768825580/Gemini_Generated_Image_5vhg335vhg335vhg_dbnxsl.png';

const formatClock = (seconds) => `${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, '0')}`;
const formatGrade = (n) => String(n).replace('.', ',');

/* Une correction est « échouée » si le backend a renvoyé une erreur (ex : modèle IA indisponible).
   Elle ne doit JAMAIS être affichée comme « parfaite ». */
const isFailedCorr = (c) => {
  if (!c) return false;
  let parsed = c;
  if (typeof c === 'string') {
    try { parsed = JSON.parse(c); } catch { return false; }
  }
  return !!(parsed && (parsed.error || parsed.correction_failed));
};

/* Feedback selon la performance */
const getFeedback = (p) => {
  if (p === 100) return { tone: 'emerald', Icon: Trophy, title: 'Excellent travail, continue !', text: 'Aucune erreur sur cet exercice. Tu maîtrises ces notions.' };
  if (p >= 80) return { tone: 'emerald', Icon: Sparkles, title: 'Très bon travail !', text: 'Il ne reste que quelques détails à peaufiner.' };
  if (p >= 60) return { tone: 'indigo', Icon: TrendingUp, title: "Bien dans l'ensemble", text: 'Mais certaines notions sont à revoir. Regarde tes erreurs de près.' };
  if (p >= 40) return { tone: 'amber', Icon: BookOpen, title: 'Des bases à consolider', text: "Reprends tes erreurs une à une. Le professeur peut t'aider." };
  return { tone: 'rose', Icon: Target, title: 'Ne lâche rien !', text: "Ce n'est qu'un début. Le professeur est là pour reprendre les notions avec toi." };
};

const tonePalette = {
  emerald: { stroke: '#10b981', soft: 'bg-emerald-50 text-emerald-600', btn: 'bg-emerald-600 shadow-emerald-600/25', confetti: ['#10b981', '#6366f1', '#f59e0b', '#ec4899'] },
  indigo: { stroke: '#6366f1', soft: 'bg-indigo-50 text-indigo-600', btn: 'bg-indigo-600 shadow-indigo-600/25', confetti: [] },
  amber: { stroke: '#f59e0b', soft: 'bg-amber-50 text-amber-600', btn: 'bg-amber-600 shadow-amber-600/25', confetti: [] },
  rose: { stroke: '#f43f5e', soft: 'bg-rose-50 text-rose-600', btn: 'bg-rose-600 shadow-rose-600/25', confetti: [] }
};

/* ============================================================
   MATHS
   ============================================================ */
const formatMathExpression = (text) => {
  if (!text) return text;
  let formatted = text;
  formatted = formatted.replace(/(\d+)\/(\d+)/g, (match, num, den) => `\\frac{${num}}{${den}}`);
  return formatted.replace(/\s+/g, ' ').trim();
};

const MathExpression = React.memo(({ children, className = '' }) => {
  const formattedText = formatMathExpression(children);
  return (
    <div className="overflow-x-auto max-w-full pb-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
      <span
        className={`math-expression font-mono text-slate-800 inline-block ${className}`}
        dangerouslySetInnerHTML={{ __html: formattedText }}
      />
    </div>
  );
});

const PureLatex = React.memo(({ math, inline, className = '' }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (containerRef.current && math) {
      try {
        let cleanExpr = math.trim().replace(/\\+$/, '');
        cleanExpr = cleanExpr.replace(/^\\displaystyle\s*/g, '');
        katex.render(cleanExpr, containerRef.current, {
          throwOnError: false,
          displayMode: !inline,
          trust: true,
          strict: false
        });
      } catch (e) {
        containerRef.current.innerText = math;
      }
    }
  }, [math, inline]);

  return <span ref={containerRef} className={`${className} ${inline ? 'inline-block px-1' : 'block my-2 text-center'}`} />;
});

const LatexRenderer = React.memo(({ expression, className = '', inline = false }) => {
  if (!expression) return null;
  const normalizedExpr = expression.normalize('NFC').replace(/D′/g, "D'").replace(/C′/g, "C'");

  if (normalizedExpr.includes('$')) {
    const parts = normalizedExpr.split('$');
    return (
      <div className={`${className} leading-relaxed`}>
        {parts.map((part, index) => (
          index % 2 === 0
            ? <span key={index} className="text-slate-700 font-medium whitespace-pre-wrap">{part}</span>
            : <PureLatex key={index} math={part} inline={true} />
        ))}
      </div>
    );
  }
  return <PureLatex math={normalizedExpr} inline={inline} className={className} />;
});

/* ============================================================
   EFFET « MACHINE À ÉCRIRE » ISOLÉ
   ============================================================ */
const Typewriter = ({ text = '', speed = 15, delay = 0, cursorClass = 'bg-indigo-400' }) => {
  const [count, setCount] = useState(0);
  const prefersReduced = typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    if (prefersReduced) { setCount(text.length); return undefined; }
    setCount(0);
    if (!text) return undefined;
    let rafId;
    const startTimer = setTimeout(() => {
      const t0 = performance.now();
      const tick = () => {
        const n = Math.min(text.length, Math.floor((performance.now() - t0) / speed) + 1);
        setCount(n);
        if (n < text.length) rafId = requestAnimationFrame(tick);
      };
      rafId = requestAnimationFrame(tick);
    }, delay);
    return () => { clearTimeout(startTimer); cancelAnimationFrame(rafId); };
  }, [text, speed, delay, prefersReduced]);

  const typing = count < text.length;

  return (
    <span className="relative block whitespace-pre-wrap">
      <span className="sr-only">{text}</span>
      <span className="invisible" aria-hidden="true">{text}</span>
      <span className="absolute inset-0" aria-hidden="true">
        {text.slice(0, count)}
        {typing && <span className={`inline-block w-1.5 h-3.5 ml-0.5 align-middle rounded-sm motion-safe:animate-pulse ${cursorClass}`} />}
      </span>
    </span>
  );
};

/* ============================================================
   TOAST COMPACT (au-dessus des widgets flottants)
   ============================================================ */
const toastTones = {
  info: 'bg-white border-slate-200 text-slate-700',
  success: 'bg-emerald-50 border-emerald-200 text-emerald-800',
  error: 'bg-rose-50 border-rose-200 text-rose-800'
};

const Toast = ({ toast }) => (
  <motion.div
    key={toast.id}
    role={toast.tone === 'error' ? 'alert' : 'status'}
    initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
    transition={{ duration: 0.22, ease: EASE_OUT }}
    className={`fixed z-30 inset-x-3 sm:inset-x-auto sm:right-4 sm:w-[380px] rounded-2xl border shadow-lg px-4 py-3 flex items-center gap-3 text-[13px] font-semibold leading-snug ${toastTones[toast.tone]}`}
    style={{ bottom: TOAST_BOTTOM }}
  >
    <span className="flex-1">{toast.text}</span>
    {toast.action && (
      <button type="button" onClick={toast.action.onClick} className="shrink-0 px-3 min-h-[36px] rounded-full bg-indigo-600 text-white text-xs font-bold active:scale-95 transition-transform touch-manipulation">
        {toast.action.label}
      </button>
    )}
    {toast.onDismiss && (
      <button type="button" onClick={toast.onDismiss} aria-label="Fermer" className="shrink-0 w-8 h-8 -mr-1 flex items-center justify-center rounded-full opacity-60 active:opacity-100 touch-manipulation">
        <X className="w-4 h-4" />
      </button>
    )}
  </motion.div>
);

/* ============================================================
   BANDEAU « LE PROFESSEUR TE CORRIGE… » (analyse en direct)
   ============================================================ */
const teacherLines = [
  'Lecture de ta copie en cours',
  'Analyse de tes réponses',
  'Repérage des points à améliorer',
  'Préparation des explications',
];

const chipStyles = {
  pending: 'bg-white/10 text-white/60',
  ok: 'bg-emerald-400/20 text-emerald-300',
  error: 'bg-rose-400/20 text-rose-300',
  failed: 'bg-amber-400/20 text-amber-300'
};

const ProfessorBanner = ({ corrected, total, statuses }) => {
  const [lineIndex, setLineIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setLineIndex((v) => (v + 1) % teacherLines.length), 2600);
    return () => clearInterval(id);
  }, []);

  const ratio = total > 0 ? Math.max(0.06, corrected / total) : 0.06;

  const [teacher] = useState(() =>
    Math.random() < 0.5
      ? {
          name: 'Alex',
          image:
            'https://res.cloudinary.com/do5zemcrk/image/upload/v1793633153/Gemini_Generated_Image_u7dpzbu7dpzbu7dp_ko0lt0.jpg',
        }
      : {
          name: 'Chloé',
          image:
            'https://res.cloudinary.com/do5zemcrk/image/upload/v1777451840/Gemini_Generated_Image_nx5sbmnx5sbmnx5s_z7u1hu.png',
        }
  );

  return (
    <motion.div
      role="status"
      aria-live="polite"
      initial={{ opacity: 0, y: -10, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.97, height: 0, marginBottom: 0 }}
      transition={{ duration: 0.3, ease: EASE_OUT }}
      className="relative isolate overflow-hidden rounded-3xl bg-slate-900 text-white p-4 shadow-xl shadow-slate-900/15 ring-1 ring-white/10"
    >
      {/* Ambiance lumineuse */}
      <div
        className="pointer-events-none absolute -top-16 -right-10 h-44 w-44 rounded-full bg-indigo-500/25 blur-3xl"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute -bottom-16 -left-10 h-40 w-40 rounded-full bg-violet-500/20 blur-3xl"
        aria-hidden="true"
      />

      <div className="relative flex items-center gap-3.5">
        {/* Photo du professeur */}
        <div className="relative h-12 w-12 shrink-0">
          {/* Ondes animées */}
          {[0, 1].map((i) => (
            <motion.span
              key={i}
              className="absolute inset-0 rounded-2xl border border-indigo-300/60"
              initial={{ scale: 1, opacity: 0.65 }}
              animate={{ scale: 1.65, opacity: 0 }}
              transition={{
                duration: 2.2,
                repeat: Infinity,
                delay: i * 1.1,
                ease: 'easeOut',
              }}
              aria-hidden="true"
            />
          ))}

          {/* Halo lumineux */}
          <div className="absolute -inset-1 rounded-2xl bg-gradient-to-br from-indigo-400/40 to-violet-500/40 blur-md" />

          {/* Image */}
          <div className="relative h-12 w-12 overflow-hidden rounded-2xl bg-white/10 ring-1 ring-white/25 shadow-lg shadow-indigo-950/30">
            <img
              src={teacher.image}
              alt={teacher.name}
              className="h-full w-full object-cover object-center scale-105"
            />
          </div>
        </div>

        {/* Informations */}
        <div className="min-w-0 flex-1">
          <p className="text-[15px] sm:text-base font-bold leading-tight tracking-tight">
            {teacher.name} te corrige
            {[0, 1, 2].map((i) => (
              <motion.span
                key={i}
                animate={{ opacity: [0.2, 1, 0.2] }}
                transition={{
                  duration: 1.2,
                  repeat: Infinity,
                  delay: i * 0.2,
                }}
                aria-hidden="true"
              >
                .
              </motion.span>
            ))}
          </p>

          <div className="relative h-5 mt-1 overflow-hidden">
            <AnimatePresence mode="wait" initial={false}>
              <motion.p
                key={lineIndex}
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -10, opacity: 0 }}
                transition={{ duration: 0.28, ease: EASE_OUT }}
                className="absolute inset-x-0 truncate text-[12px] sm:text-[13px] text-indigo-100/75"
              >
                {teacherLines[lineIndex]}
              </motion.p>
            </AnimatePresence>
          </div>
        </div>

        {/* Compteur */}
        <div className="shrink-0 rounded-xl border border-white/10 bg-white/[0.08] px-2.5 py-1.5 backdrop-blur-sm">
          <span className="font-mono text-xs font-bold tabular-nums tracking-tight">
            {corrected}
            <span className="text-white/40">/{total}</span>
          </span>
        </div>
      </div>

      {/* Barre de progression */}
      <div className="relative mt-4 h-1.5 overflow-hidden rounded-full bg-white/10">
        <div
          className="absolute inset-0 origin-left rounded-full bg-gradient-to-r from-indigo-400 via-violet-400 to-fuchsia-400 transition-transform duration-700 ease-out"
          style={{ transform: `scaleX(${ratio})` }}
        />

        {/* Reflet animé */}
        <motion.span
          className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/40 to-transparent"
          initial={{ x: '-100%' }}
          animate={{ x: '300%' }}
          transition={{
            duration: 1.8,
            repeat: Infinity,
            ease: 'linear',
          }}
          aria-hidden="true"
        />
      </div>

      {/* Pastilles des questions */}
      {statuses.length > 0 && (
        <div
          className="relative mt-3.5 flex flex-wrap gap-1.5"
          aria-hidden="true"
        >
          {statuses.map(({ id, status }) => (
            <span
              key={id}
              className={`h-6 min-w-[2.25rem] px-2 rounded-full text-[11px] font-bold flex items-center justify-center transition-colors duration-300 ${chipStyles[status]}`}
            >
              <motion.span
                key={status}
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{
                  type: 'spring',
                  stiffness: 500,
                  damping: 20,
                }}
                className="flex items-center gap-1"
              >
                {status === 'ok' && <Check className="w-3 h-3" />}
                {status === 'error' && <X className="w-3 h-3" />}
                {status === 'failed' && (
                  <AlertTriangle className="w-3 h-3" />
                )}
                {status === 'pending' && (
                  <span className="w-1.5 h-1.5 rounded-full bg-white/50 motion-safe:animate-pulse" />
                )}
                {id}
              </motion.span>
            </span>
          ))}
        </div>
      )}
    </motion.div>
  );
};

/* ============================================================
   MODALE DE NOTE (temporaire, centrée)
   ============================================================ */
const GradeModal = ({ percentage, grade, correct, total, failed, onClose }) => {
  const fb = getFeedback(percentage);
  const palette = tonePalette[fb.tone];
  const [shown, setShown] = useState(0);
  const R = 52;
  const C = 2 * Math.PI * R;

  const confetti = useMemo(() => {
    if (!palette.confetti.length) return [];
    return Array.from({ length: 16 }, (_, i) => {
      const angle = (i / 16) * Math.PI * 2;
      const dist = 105 + (i % 3) * 26;
      return {
        x: Math.cos(angle) * dist, y: Math.sin(angle) * dist,
        size: 6 + (i % 3) * 2, color: palette.confetti[i % palette.confetti.length], round: i % 2 === 0
      };
    });
  }, [palette]);

  // Compteur animé de la note
  useEffect(() => {
    const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) { setShown(grade); return undefined; }
    let rafId;
    const t0 = performance.now();
    const tick = () => {
      const p = Math.min(1, (performance.now() - t0) / 1000);
      const eased = 1 - Math.pow(1 - p, 3);
      setShown(Math.round(grade * eased * 2) / 2);
      if (p < 1) rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [grade]);

  // Fermeture automatique + Échap
  useEffect(() => {
    const id = setTimeout(onClose, GRADE_MODAL_MS);
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { clearTimeout(id); window.removeEventListener('keydown', onKey); };
  }, [onClose]);

  return (
    <motion.div
      role="dialog" aria-modal="true" aria-label="Ta note"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={onClose}
      className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-slate-900/45 backdrop-blur-[2px]"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.8, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.94, y: 8 }}
        transition={{ type: 'spring', stiffness: 380, damping: 26 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-xs rounded-[28px] bg-white px-6 pt-7 pb-6 text-center shadow-2xl"
      >
        {/* Anneau de note */}
        <div className="relative mx-auto w-36 h-36">
          {confetti.map((c, i) => (
            <motion.span
              key={i}
              className="absolute left-1/2 top-1/2"
              style={{ width: c.size, height: c.size, marginLeft: -c.size / 2, marginTop: -c.size / 2, background: c.color, borderRadius: c.round ? '9999px' : '2px' }}
              initial={{ x: 0, y: 0, opacity: 0, scale: 0.3 }}
              animate={{ x: c.x, y: c.y, opacity: [0, 1, 0], scale: 1, rotate: 180 }}
              transition={{ duration: 1.1, delay: 0.35, ease: EASE_OUT }}
              aria-hidden="true"
            />
          ))}
          <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90" aria-hidden="true">
            <circle cx="60" cy="60" r={R} fill="none" stroke="#f1f5f9" strokeWidth="9" />
            <motion.circle
              cx="60" cy="60" r={R} fill="none" stroke={palette.stroke} strokeWidth="9" strokeLinecap="round"
              strokeDasharray={C}
              initial={{ strokeDashoffset: C }} animate={{ strokeDashoffset: C * (1 - percentage / 100) }}
              transition={{ duration: 1, ease: EASE_OUT, delay: 0.15 }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-[34px] font-extrabold tracking-tight tabular-nums text-slate-900 leading-none">{formatGrade(shown)}</span>
            <span className="mt-1 text-xs font-semibold text-slate-400">sur 20</span>
          </div>
        </div>

        <div className={`mx-auto mt-4 w-9 h-9 rounded-full flex items-center justify-center ${palette.soft}`}>
          <fb.Icon className="w-[18px] h-[18px]" />
        </div>
        <h3 className="mt-2.5 text-[19px] font-extrabold tracking-tight text-slate-900 leading-tight">{fb.title}</h3>
        <p className="mt-1.5 text-[14px] text-slate-500 leading-relaxed">{fb.text}</p>
        <p className="mt-3 text-[12px] font-semibold text-slate-400 tabular-nums">
          {correct}/{total} questions réussies
          {failed > 0 && ` · ${failed} correction${failed > 1 ? 's' : ''} indisponible${failed > 1 ? 's' : ''}`}
        </p>

        <button
          type="button" onClick={onClose}
          className={`mt-5 w-full min-h-[48px] rounded-2xl text-white text-[15px] font-bold shadow-lg active:scale-[0.98] transition-transform touch-manipulation ${palette.btn}`}
        >
          Voir ma correction
        </button>

        {/* Minuteur de fermeture automatique */}
        <div className="absolute bottom-0 inset-x-0 h-1 rounded-b-[28px] overflow-hidden" aria-hidden="true">
          <motion.div
            className="h-full origin-left"
            style={{ background: palette.stroke, opacity: 0.55 }}
            initial={{ scaleX: 1 }} animate={{ scaleX: 0 }}
            transition={{ duration: GRADE_MODAL_MS / 1000, ease: 'linear' }}
          />
        </div>
      </motion.div>
    </motion.div>
  );
};

/* ============================================================
   WIDGET D'APPEL
   ============================================================ */
const Dock = ({ children }) => (
  <div className="fixed left-3 z-50 flex flex-col items-start gap-2.5 pointer-events-none" style={{ bottom: DOCK_BOTTOM }}>
    {children}
  </div>
);

/* État repos : un simple bouton, sans animation */
const IdleCallWidget = ({ onClick, disabled }) => (
  <Dock>
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label="Parler au professeur"
      className="pointer-events-auto h-12 px-5 rounded-full bg-indigo-600 text-white shadow-lg shadow-indigo-600/25 flex items-center justify-center gap-2 text-[14px] font-bold active:scale-95 transition-transform touch-manipulation disabled:opacity-60"
    >
      <Phone className="w-[18px] h-[18px] shrink-0" />
      Parler au prof
    </button>
  </Dock>
);

/* Niveau sonore (0-100) de la voix du professeur */
const useAgentVolume = (audioTrack) => {
  const [volume, setVolume] = useState(0);

  useEffect(() => {
    const mediaTrack = audioTrack?.publication?.track?.mediaStreamTrack;
    const Ctx = typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext);
    if (!mediaTrack || !Ctx) { setVolume(0); return undefined; }

    const ctx = new Ctx();
    ctx.resume?.().catch(() => {});
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    const source = ctx.createMediaStreamSource(new MediaStream([mediaTrack]));
    source.connect(analyser); // pas connecté à destination : aucun son en double
    const data = new Uint8Array(analyser.frequencyBinCount);

    let rafId;
    let last = 0;
    const tick = (t) => {
      if (t - last > 50) {
        last = t;
        analyser.getByteFrequencyData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) sum += data[i];
        setVolume(Math.min(100, ((sum / data.length) / 255) * 100 * 2.2));
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafId);
      try { source.disconnect(); } catch (e) { /* noop */ }
      ctx.close?.().catch(() => {});
    };
  }, [audioTrack]);

  return volume;
};

/* Vignette du professeur pendant l'appel (en bas à droite, au-dessus du bouton « Terminer ») */
const ActiveCall = ({ phase, activeVoice, timeLeft, volume = 0, onHangup }) => {
  const showLoader = phase === 'dialing' || phase === 'preparing' || phase === 'slow';
  const isSpeaking = phase === 'speaking';
  const loaderLabel = phase === 'dialing' ? 'Appel...' : phase === 'slow' ? 'Connexion...' : 'Prépare...';
  const stateLabel = isSpeaking ? 'Il parle' : phase === 'thinking' ? 'Réfléchit' : 'À toi';

  return (
    <motion.div
      role="region"
      aria-label="Appel avec le professeur"
      aria-live="polite"
      initial={{ opacity: 0, scale: 0.5, y: 50, rotate: 5 }}
      animate={{ opacity: 1, scale: 1, y: 0, rotate: 0 }}
      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
      className="fixed z-50 bottom-[90px] right-4 w-28 h-40 sm:w-32 sm:h-48 rounded-[24px] overflow-hidden shadow-[0_20px_40px_-10px_rgba(0,0,0,0.3)] border-[3px] border-slate-900 bg-slate-900"
      style={{ marginBottom: 'env(safe-area-inset-bottom)' }}
    >
      <img
        src={activeVoice === 'Kore' ? KORE_IMG : PUCK_IMG}
        alt="Le professeur"
        className={`absolute inset-0 z-0 w-full h-full object-cover transition-all duration-700 ${showLoader ? 'blur-md scale-110 opacity-50' : 'blur-0 scale-100 opacity-100'}`}
      />

      {/* Voile sombre */}
      <div className="absolute inset-0 z-10 bg-gradient-to-t from-black/80 via-black/10 to-black/30 pointer-events-none" />

      {/* Loader */}
      {showLoader && (
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center p-2 text-center pointer-events-none">
          <Loader2 className="w-6 h-6 text-white animate-spin mb-2" />
          <span className="text-[9px] text-white/80 font-bold uppercase tracking-widest leading-tight">{loaderLabel}</span>
        </div>
      )}

      {/* Raccrocher (toujours accessible) */}
      <button
        type="button"
        onClick={onHangup}
        aria-label="Raccrocher"
        className="absolute top-2 left-2 z-30 w-8 h-8 rounded-full bg-rose-500 text-white shadow-lg flex items-center justify-center active:scale-90 transition-transform touch-manipulation"
      >
        <PhoneOff className="w-4 h-4" />
      </button>

      {/* Minuteur + état (quand connecté) */}
      {!showLoader && (
        <div className="absolute inset-0 z-20 flex flex-col justify-between p-2 pointer-events-none">
          <div className="flex justify-end">
            <div className={`px-2 py-0.5 rounded-full text-[10px] font-mono font-bold backdrop-blur-md ${
              timeLeft <= 30 ? 'bg-rose-500 text-white animate-pulse' : 'bg-black/50 text-white/90'
            }`}>
              {formatClock(timeLeft)}
            </div>
          </div>

          <div className="flex flex-col items-center gap-1.5 pb-1">
            <div className="flex items-end gap-0.5 h-8">
              {[0.7, 1, 0.8, 0.5].map((multiplier, i) => (
                <motion.div
                  key={i}
                  animate={{ height: isSpeaking ? Math.min(30, Math.max(3, 3 + volume * multiplier * 0.3)) : 3 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                  className={`w-1 rounded-full ${isSpeaking ? 'bg-white' : 'bg-white/40'}`}
                />
              ))}
            </div>
            <span className={`text-[8px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full backdrop-blur-sm ${
              isSpeaking ? 'bg-indigo-500/80 text-white' : phase === 'listening' ? 'bg-emerald-500/80 text-white' : 'bg-black/40 text-white/70'
            }`}>
              {stateLabel}
            </span>
          </div>
        </div>
      )}
    </motion.div>
  );
};

/* Appel affiché pendant l'obtention du token (avant que LiveKit n'existe) */
const DialingCall = ({ activeVoice, onHangup }) => (
  <ActiveCall phase="dialing" activeVoice={activeVoice} timeLeft={CALL_MAX_SECONDS} onHangup={onHangup} />
);

/* ============================================================
   OVERLAY LIVEKIT (dans <LiveKitRoom>)
   ============================================================ */
const LiveKitImmersionOverlay = ({ activeVoice, onForceDisconnect, onAgentTimeout, onTimeUp }) => {
  const roomState = useConnectionState();
  const { state: agentState, audioTrack } = useVoiceAssistant();
  const volume = useAgentVolume(audioTrack);

  const isConnected = roomState === ConnectionState.Connected;
  const isAgentReady = agentState === 'listening' || agentState === 'speaking';
  const [hasAgentBeenReady, setHasAgentBeenReady] = useState(false);
  const [isTakingLong, setIsTakingLong] = useState(false);
  const [timeLeft, setTimeLeft] = useState(CALL_MAX_SECONDS);

  useEffect(() => { if (isAgentReady) setHasAgentBeenReady(true); }, [isAgentReady]);

  useEffect(() => {
    if (hasAgentBeenReady && agentState === 'disconnected' && onForceDisconnect) onForceDisconnect();
  }, [agentState, hasAgentBeenReady, onForceDisconnect]);

  // Délais de connexion de l'agent
  useEffect(() => {
    let longTimerId, timeoutId;
    if (isConnected && !isAgentReady && !hasAgentBeenReady) {
      longTimerId = setTimeout(() => setIsTakingLong(true), 15000);
      timeoutId = setTimeout(() => { if (onAgentTimeout) onAgentTimeout(); }, 45000);
    }
    return () => { clearTimeout(timeoutId); clearTimeout(longTimerId); setIsTakingLong(false); };
  }, [isConnected, isAgentReady, hasAgentBeenReady, onAgentTimeout]);

  // Chrono basé sur l'heure de fin : fiable même si l'onglet est ralenti
  useEffect(() => {
    if (!hasAgentBeenReady) return undefined;
    const endAt = Date.now() + CALL_MAX_SECONDS * 1000;
    const id = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((endAt - Date.now()) / 1000));
      setTimeLeft(remaining);
      if (remaining <= 0) {
        clearInterval(id);
        if (onTimeUp) onTimeUp();
        else if (onForceDisconnect) onForceDisconnect();
      }
    }, 1000);
    return () => clearInterval(id);
  }, [hasAgentBeenReady, onForceDisconnect, onTimeUp]);

  let phase;
  if (!isConnected && !hasAgentBeenReady) phase = 'dialing';
  else if (!hasAgentBeenReady) phase = isTakingLong ? 'slow' : 'preparing';
  else if (agentState === 'speaking') phase = 'speaking';
  else if (agentState === 'thinking') phase = 'thinking';
  else phase = 'listening';

  return (
    <ActiveCall
      phase={phase} activeVoice={activeVoice} timeLeft={timeLeft}
      volume={volume}
      onHangup={onForceDisconnect}
    />
  );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
const SmartExInner = ({ data, onBack, isProgressive = false, studentName = null, homeworkId = null }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [firstErrorMap, setFirstErrorMap] = useState({});
  const [expandedQuestions, setExpandedQuestions] = useState({});
  const [showSolution, setShowSolution] = useState({});
  const [solutionText, setSolutionText] = useState({});
  const [highlightErrors, setHighlightErrors] = useState({});

  const [progressiveData, setProgressiveData] = useState(data);
  const [analysisStatus, setAnalysisStatus] = useState('completed');
  const [currentMessage, setCurrentMessage] = useState('');

  const [showCallNotification, setShowCallNotification] = useState(false);
  const [showGrade, setShowGrade] = useState(false);
  const [gradeSeen, setGradeSeen] = useState(false);

  const [liveKitToken, setLiveKitToken] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [activeVoice, setActiveVoice] = useState(null);

  const [callStatus, setCallStatus] = useState('idle');
  const [showCompletionToast, setShowCompletionToast] = useState(false);
  const [agentConnectionError, setAgentConnectionError] = useState(false);
  const [isTimeUp, setIsTimeUp] = useState(false);

  const [liveKitUrl] = useState(process.env.REACT_APP_LIVEKIT_URL || 'wss://spyrale-partner-0ds4k9om.livekit.cloud');

  const audioRefs = useRef({});
  const stepRefs = useRef({});
  const pollingInterval = useRef(null);
  const pendingTimeouts = useRef([]);
  const gradeShownRef = useRef(false);
  const callAttemptRef = useRef(0);

  // Timers locaux nettoyés au démontage
  const later = useCallback((fn, ms) => {
    const id = setTimeout(fn, ms);
    pendingTimeouts.current.push(id);
    return id;
  }, []);
  useEffect(() => () => { pendingTimeouts.current.forEach(clearTimeout); }, []);

  const isPartner = useMemo(() => {
    try { return JSON.parse(sessionStorage.getItem('user_info') || '{}').role === 'student_partner'; }
    catch { return false; }
  }, []);

  const currentData = progressiveData || data;
  const totalQuestions = Object.keys(currentData?.etape_1_student_solutions || {}).length;
  const correctedQuestions = Object.keys(currentData?.etape_2_corrections || {}).length;

  const corrections = currentData?.etape_2_corrections;

  // Questions avec une vraie erreur détectée (hors corrections échouées)
  const errorQuestions = useMemo(() => Object.values(corrections || {}).filter((c) => {
    try {
      const parsed = typeof c === 'string' ? JSON.parse(c) : c;
      return !isFailedCorr(parsed) && parsed.first_error != null;
    } catch { return false; }
  }).length, [corrections]);

  // Questions dont la correction a échoué côté serveur
  const failedQuestions = useMemo(
    () => Object.values(corrections || {}).filter(isFailedCorr).length,
    [corrections]
  );

  const correctQuestions = correctedQuestions - errorQuestions - failedQuestions;
  const totalQuestionsInExercise = Object.keys(currentData?.exercise_statement || {}).filter((key) => key.startsWith('Q')).length;
  const gradeDenominator = totalQuestionsInExercise || totalQuestions;
  const percentage = gradeDenominator > 0 ? Math.round((correctQuestions / gradeDenominator) * 100) : 0;
  const grade20 = Math.round((percentage / 100) * 20 * 2) / 2; // note sur 20 au demi-point

  const isProcessing = isProgressive && analysisStatus === 'processing';
  const gradeReady = !isProcessing && gradeDenominator > 0;
  const gradeColor = percentage >= 80 ? 'text-emerald-600' : percentage >= 50 ? 'text-amber-600' : 'text-rose-600';

  // Seuil d'affichage du bouton d'appel :
  // - tout juste (aucune erreur)  → pas de bouton
  // - tout faux (aucune réussite) → pas de bouton (l'élève doit revoir le cours)
  // - au moins une réussite ET au moins une erreur → bouton affiché
  const showCallButton = analysisStatus === 'completed' && errorQuestions >= 1 && correctQuestions >= 1 && callStatus !== 'completed';
  const isCalling = callStatus === 'calling';

  /* ---------------- ERREURS ET ÉTAPES (déclarées avant les effets qui les utilisent) ---------------- */
  const parseFirstErrors = (dataToParser = progressiveData) => {
    const firstErrors = {};
    if (!dataToParser?.etape_2_corrections) return firstErrors;
    Object.entries(dataToParser.etape_2_corrections).forEach(([questionId, correctionValue]) => {
      try {
        const correctionData = typeof correctionValue === 'string'
          ? JSON.parse(correctionValue)
          : correctionValue;
        if (isFailedCorr(correctionData)) return; // une correction échouée n'est pas une « erreur de l'élève »
        if (correctionData.first_error && correctionData.first_error !== null) {
          firstErrors[questionId] = {
            line: correctionData.first_error.line,
            explanation: correctionData.first_error.explanation
          };
        }
      } catch (e) {
        console.error('Erreur parsing correction', e);
      }
    });
    return firstErrors;
  };

  /* ---------------- EFFETS ---------------- */
  // Modale de note : une seule fois, quand tout est analysé
  useEffect(() => {
    if (gradeShownRef.current) return undefined;
    if (analysisStatus !== 'completed' || !isLoaded) return undefined;
    if (correctedQuestions === 0 || failedQuestions >= correctedQuestions) return undefined;
    const timer = setTimeout(() => {
      gradeShownRef.current = true;
      setShowGrade(true);
      setGradeSeen(true);
    }, 900);
    return () => clearTimeout(timer);
  }, [analysisStatus, isLoaded, correctedQuestions, failedQuestions]);

  // Invitation à appeler le prof : après la modale de note
  useEffect(() => {
    if (showCallButton && callStatus === 'idle' && gradeSeen && !showGrade) {
      setShowCallNotification(true);
      const timer = setTimeout(() => setShowCallNotification(false), 8000);
      return () => clearTimeout(timer);
    }
  }, [showCallButton, callStatus, gradeSeen, showGrade]);

  useEffect(() => {
    if (callStatus === 'completed') {
      setShowCompletionToast(true);
      const timer = setTimeout(() => setShowCompletionToast(false), 10000);
      return () => clearTimeout(timer);
    }
  }, [callStatus]);

  useEffect(() => {
    if (agentConnectionError) {
      const timer = setTimeout(() => setAgentConnectionError(false), 8000);
      return () => clearTimeout(timer);
    }
  }, [agentConnectionError]);

  useEffect(() => {
    setProgressiveData(data);
    if (data) setFirstErrorMap(parseFirstErrors(data));
    const timer = setTimeout(() => setIsLoaded(true), 300);
    return () => clearTimeout(timer);
  }, [data]);

  useEffect(() => {
    if (isProgressive) {
      if (data && (data.status === 'completed' || !data.status)) {
        setAnalysisStatus('completed');
        setProgressiveData(data);
        setFirstErrorMap(parseFirstErrors(data));
        return;
      }
      setAnalysisStatus('processing');
      const userInfo = JSON.parse(sessionStorage.getItem('user_info') || '{}');
      const userId = userInfo.id;

      if (!userId) {
        setAnalysisStatus('error');
        setCurrentMessage('Erreur: Informations utilisateur manquantes');
        return;
      }

      let errorCount = 0;
      const fetchProgress = async () => {
        try {
          const uniqueUrl = `${API_URLS.GET_PROGRESS(userId)}?t=${Date.now()}`;
          const response = await fetch(uniqueUrl, {
            headers: {
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache',
              'Expires': '0'
            }
          });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const progressData = await response.json();

          if (progressData.status === 'error' && progressData.message && !progressData.message.includes('❌')) {
            throw new Error('Fichier de progression temporairement verrouillé par le système');
          }
          errorCount = 0;

          if (progressData.status === 'completed') {
            setProgressiveData(progressData);
            setFirstErrorMap(parseFirstErrors(progressData));
            setAnalysisStatus('completed');
            setCurrentMessage('Analyse terminée');
            if (pollingInterval.current) {
              clearInterval(pollingInterval.current);
              pollingInterval.current = null;
            }
          } else if (progressData.status === 'error') {
            setAnalysisStatus('error');
            setCurrentMessage(progressData.message || "L'analyse a rencontré un problème technique.");
            if (pollingInterval.current) clearInterval(pollingInterval.current);
          } else {
            setProgressiveData(progressData);
            setFirstErrorMap(parseFirstErrors(progressData));
            setCurrentMessage(progressData.message || 'Analyse en cours...');
          }
        } catch (error) {
          console.warn('⚠️ Micro-coupure ignorée:', error.message);
          errorCount++;
          if (errorCount >= 5) {
            setAnalysisStatus('error');
            setCurrentMessage('Connexion instable ou perdue avec le serveur.');
            if (pollingInterval.current) clearInterval(pollingInterval.current);
          }
        }
      };

      fetchProgress();
      pollingInterval.current = setInterval(fetchProgress, 2000);
      return () => {
        if (pollingInterval.current) clearInterval(pollingInterval.current);
      };
    }
  }, [isProgressive, studentName, data]);

  /* ---------------- APPEL VOCAL (logique inchangée) ---------------- */
  const stopAllAudios = useCallback(() => {
    Object.keys(audioRefs.current).forEach((key) => {
      const audioElement = audioRefs.current[key];
      if (audioElement && !audioElement.paused) {
        audioElement.pause();
        audioElement.currentTime = 0;
      }
    });
  }, []);

  const handleStartHelp = useCallback(async () => {
    const attemptId = ++callAttemptRef.current;
    try {
      stopAllAudios();
      setShowCallNotification(false);
      setAgentConnectionError(false);
      setIsTimeUp(false);
      setCallStatus('calling');
      setIsConnecting(true);

      const voices = ['Puck', 'Kore'];
      const randomVoice = voices[Math.floor(Math.random() * voices.length)];
      setActiveVoice(randomVoice);

      const analysisData = progressiveData || data;
      const cleanData = {
        student_name: analysisData?.student_name || 'Étudiant',
        exercice_context: analysisData?.exercise_context,
        solutions_eleve: analysisData?.etape_1_student_solutions,
        corrections: analysisData?.etape_2_corrections,
        message_global: "L'élève demande de l'aide sur cet exercice."
      };

      const url = `${API_URLS.BASE_URL}/api/get-token?student_name=${analysisData?.student_name || 'Eleve'}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ voice: randomVoice, context: cleanData })
      });

      if (!response.ok) {
        const errText = await response.text();
        console.error('🔴 [FRONTEND] Erreur API Token :', response.status, errText);
        throw new Error('Erreur génération token LiveKit');
      }

      const responseData = await response.json();
      if (attemptId !== callAttemptRef.current) return; // appel annulé entre-temps
      setLiveKitToken(responseData.token);
    } catch (error) {
      if (attemptId !== callAttemptRef.current) return;
      console.error('🔴 [FRONTEND] Erreur globale lancement appel:', error);
      setIsConnecting(false);
      setCallStatus('idle');
      setAgentConnectionError(true);
    }
  }, [progressiveData, data, stopAllAudios]);

  const handleStopHelp = useCallback(() => {
    callAttemptRef.current++;
    setLiveKitToken(null);
    setIsConnecting(false);
    setActiveVoice(null);
    setCallStatus('completed');
  }, []);

  // Raccrocher avant même que le prof ne décroche : on revient simplement à l'état initial
  const handleCancelDial = useCallback(() => {
    callAttemptRef.current++;
    setLiveKitToken(null);
    setIsConnecting(false);
    setActiveVoice(null);
    setCallStatus('idle');
  }, []);

  const handleTimeUp = useCallback(() => {
    setLiveKitToken(null);
    setIsConnecting(false);
    setActiveVoice(null);
    setCallStatus('completed');
    setIsTimeUp(true);
  }, []);

  const handleAgentTimeout = useCallback(() => {
    setLiveKitToken(null);
    setIsConnecting(false);
    setActiveVoice(null);
    setCallStatus('idle');
    setAgentConnectionError(true);
  }, []);

  /* ---------------- ERREURS ET ÉTAPES ---------------- */
  const getFirstErrorStep = (questionId) => firstErrorMap[questionId]?.line || null;
  const getFirstErrorExplanation = (questionId) => firstErrorMap[questionId]?.explanation || null;
  const hasQuestionErrors = (questionId) => firstErrorMap[questionId] !== undefined;
  const convertLineToFrench = (line) => (line ? `${line.replace('L', '')}` : '');

  const shouldStepBeRed = (questionId, stepId) => {
    const firstErrorStep = getFirstErrorStep(questionId);
    if (!firstErrorStep) return false;
    const stepNumber = parseInt(stepId.replace('L', ''));
    const firstErrorNumber = parseInt(firstErrorStep.replace('L', ''));
    return stepNumber >= firstErrorNumber;
  };

  const isFirstErrorStep = (questionId, stepId) => {
    const firstErrorStep = getFirstErrorStep(questionId);
    return firstErrorStep && stepId === firstErrorStep;
  };

  // Statut d'une question : pending | ok | error | failed
  const getQuestionStatus = (questionId) => {
    const corr = currentData?.etape_2_corrections?.[questionId];
    if (!corr) return 'pending';
    if (isFailedCorr(corr)) return 'failed';
    return hasQuestionErrors(questionId) ? 'error' : 'ok';
  };

  const questionStatuses = Object.keys(currentData?.etape_1_student_solutions || {}).map((id) => ({ id, status: getQuestionStatus(id) }));

  const handleGoToChapters = async () => {
    if (callStatus === 'calling') handleStopHelp();
    try {
      const userInfo = JSON.parse(sessionStorage.getItem('user_info') || '{}');
      const userId = userInfo.id;
      if (userInfo.role === 'student_partner' && homeworkId && userId) {
        const { error: dbError } = await supabase
          .from('homework_submissions')
          .upsert({
            homework_id: homeworkId,
            student_id: userId,
            status: 'COMPLETED',
            evaluations: {
              score: percentage,
              correct_questions: correctQuestions,
              total_questions: totalQuestionsInExercise,
              completed_at: new Date().toISOString()
            }
          }, { onConflict: 'homework_id, student_id' });
        if (dbError) console.error('❌ Erreur db:', dbError);
      }
      if (userId) {
        const formData = new FormData();
        formData.append('user_id', userId);
        await fetch(`${API_URLS.BASE_URL}/cleanup-session`, { method: 'POST', body: formData });
      }
    } catch (error) {
      console.error('❌ Erreur sortie:', error);
    } finally {
      if (onBack) onBack();
    }
  };

  const formatExplanation = (text) => {
    if (!text) return '';
    return text.replace(/\^2/g, '²').replace(/\^3/g, '³').replace(/\*/g, '×').replace(/ - /g, ' − ').replace(/-(\d)/g, '−$1');
  };

  const scrollToFirstError = (questionId) => {
    const firstErrorStep = getFirstErrorStep(questionId);
    if (!firstErrorStep) return;
    const checkAndScroll = () => {
      const element = stepRefs.current[`${questionId}-${firstErrorStep}`];
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightErrors((prev) => ({ ...prev, [questionId]: true }));
        later(() => setHighlightErrors((prev) => ({ ...prev, [questionId]: false })), 2000);
      }
    };
    later(checkAndScroll, 100);
    later(checkAndScroll, 500);
  };

  const toggleSolution = (questionId) => {
    if (showSolution[questionId]) {
      setShowSolution((prev) => ({ ...prev, [questionId]: false }));
      setSolutionText((prev) => ({ ...prev, [questionId]: '' }));
      return;
    }
    setShowSolution((prev) => ({ ...prev, [questionId]: true }));
    try {
      const cd = progressiveData || data;
      const correctionRaw = cd?.etape_2_corrections?.[questionId];
      let correctionData;
      if (typeof correctionRaw === 'string') {
        try { correctionData = JSON.parse(correctionRaw); }
        catch (e) { correctionData = { correction_stepBystep: correctionRaw }; }
      } else {
        correctionData = correctionRaw;
      }

      // Correction échouée côté serveur : on ne prétend pas que la réponse est juste
      if (isFailedCorr(correctionData)) {
        setSolutionText((prev) => ({ ...prev, [questionId]: 'Correction indisponible pour cette question.' }));
        return;
      }

      const hasErrors = correctionData?.first_error !== null && correctionData?.first_error !== undefined;
      let text;

      if (hasErrors && correctionData?.correction_stepBystep) {
        text = correctionData.correction_stepBystep;
      } else if (!hasErrors) {
        const solutionsMap = cd?.reference_solutions || {};
        let referenceText = solutionsMap[questionId];
        if (!referenceText) {
          const studentKeys = Object.keys(cd?.etape_1_student_solutions || {});
          const refKeys = Object.keys(solutionsMap);
          const qIndex = studentKeys.indexOf(questionId);
          if (qIndex !== -1 && qIndex < refKeys.length) referenceText = solutionsMap[refKeys[qIndex]];
        }
        referenceText = referenceText || 'Pas de référence disponible.';
        text = `✅ Solution correcte.\n\nRéférence :\n${referenceText}`;
      } else {
        text = 'Correction en cours de génération...';
      }
      setSolutionText((prev) => ({ ...prev, [questionId]: formatExplanation(text) }));
    } catch (e) {
      setSolutionText((prev) => ({ ...prev, [questionId]: 'Erreur chargement correction.' }));
    }
  };

  const toggleQuestion = (questionId) => {
    const wasExpanded = expandedQuestions[questionId];
    if (wasExpanded) {
      setExpandedQuestions({});
    } else {
      setExpandedQuestions({ [questionId]: true });
      if (hasQuestionErrors(questionId)) scrollToFirstError(questionId);
    }
  };

  const prepareMathContent = (content) => {
    if (!content) return '';
    let str = content.toString().trim();
    str = str.replace(/\\+\s*$/g, '');
    str = str.replace(/\u00A0/g, ' ');
    if (!str.includes('\\text')) str = str.replace(/ /g, '\\ ');
    return str;
  };

  const closeGrade = useCallback(() => setShowGrade(false), []);

  /* ---------------- TOAST (un seul à la fois, par priorité) ---------------- */
  let toast = null;
  if (!isCalling && !showGrade) {
    if (agentConnectionError) {
      toast = { id: 'err', tone: 'error', text: 'Impossible de joindre le professeur. Réessaie dans un instant.', onDismiss: () => setAgentConnectionError(false) };
    } else if (showCompletionToast || isTimeUp) {
      toast = { id: 'done', tone: 'success', text: isTimeUp ? 'Le temps est écoulé. Tu peux valider ton devoir.' : 'Appel terminé. Tu peux valider ton devoir.', onDismiss: () => { setShowCompletionToast(false); setIsTimeUp(false); } };
    } else if (showCallNotification && showCallButton) {
      toast = { id: 'call', tone: 'info', text: "Une erreur ? Le professeur peut te l'expliquer à l'oral.", action: { label: 'Appeler', onClick: () => { setShowCallNotification(false); handleStartHelp(); } }, onDismiss: () => setShowCallNotification(false) };
    }
  }

  /* ---------------- RENDU ---------------- */
  if (!progressiveData && !data && analysisStatus !== 'error') {
    return <div className="p-10 text-center text-slate-500 font-sans">Aucune donnée d'analyse disponible.</div>;
  }

  return (
    <div
      className="min-h-[100dvh] bg-slate-50 font-sans text-slate-900 selection:bg-indigo-200 selection:text-indigo-900 relative antialiased"
      style={{ paddingBottom: `calc(max(env(safe-area-inset-bottom), 0.5rem) + ${isCalling ? '300px' : '110px'})` }}
    >
      <style dangerouslySetInnerHTML={{ __html: `
        button { -webkit-tap-highlight-color: transparent; }
        @keyframes spyShimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
        .spy-shimmer-text { background: linear-gradient(90deg, #94a3b8 0%, #4f46e5 50%, #94a3b8 100%); background-size: 200% 100%; -webkit-background-clip: text; background-clip: text; color: transparent; animation: spyShimmer 1.8s linear infinite; }
        @media (prefers-reduced-motion: reduce) { .spy-shimmer-text { animation: none; background: none; color: #64748b; } }
      ` }} />

      {/* LiveKit (logique invisible + vignette du professeur) */}
      {liveKitToken && liveKitUrl && callStatus === 'calling' && !agentConnectionError && (
        <LiveKitRoom
          serverUrl={liveKitUrl}
          token={liveKitToken}
          connect={true}
          audio={true}
          onConnected={() => console.log('🟢 [FRONTEND] Connecté au serveur LiveKit')}
          onDisconnected={() => {
            console.log('🟡 [FRONTEND] Déconnecté de LiveKit.');
            setCallStatus('completed');
            setLiveKitToken(null);
            setIsConnecting(false);
          }}
          onError={(error) => {
            console.error('🔴 [FRONTEND] ERREUR WEBSOCKET LIVEKIT :', error);
            handleAgentTimeout();
          }}
        >
          <RoomAudioRenderer />
          <LiveKitImmersionOverlay
            activeVoice={activeVoice}
            onForceDisconnect={handleStopHelp}
            onAgentTimeout={handleAgentTimeout}
            onTimeUp={handleTimeUp}
          />
        </LiveKitRoom>
      )}

      {/* Vignette pendant l'obtention du token (avant que LiveKit existe) */}
      {isCalling && !liveKitToken && !agentConnectionError && (
        <DialingCall activeVoice={activeVoice} onHangup={handleCancelDial} />
      )}

      {/* Header compact, opaque, avec la note */}
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200/70" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <div className="max-w-4xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="https://res.cloudinary.com/do5zemcrk/image/upload/v1762868623/WhatsApp_Image_2025-11-11_at_14.25.45_bbfsgq.jpg"
              alt="Spyrale" width="32" height="32" decoding="async"
              className="w-8 h-8 rounded-lg object-cover shadow-sm border border-slate-100"
            />
            <div className="flex flex-col">
              <h1 className="text-sm font-black tracking-tight text-slate-900 leading-none">SPYRALE Ultimate</h1>
              <span className="text-[11px] text-slate-500 font-medium mt-0.5">Correction</span>
            </div>
          </div>

          <div
            className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-slate-50 pl-3 pr-3.5 h-9"
            aria-label={gradeReady ? `Note : ${formatGrade(grade20)} sur 20` : 'Note en cours de calcul'}
          >
            <span className="text-[11px] font-semibold text-slate-400">Note</span>
            <span className="h-4 w-px bg-slate-200" aria-hidden="true" />
            {gradeReady ? (
              <motion.span
                key={grade20}
                initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, ease: EASE_OUT }}
                className={`text-[17px] font-extrabold tabular-nums leading-none ${gradeColor}`}
              >
                {formatGrade(grade20)}
                <span className="text-xs font-semibold text-slate-400 ml-0.5">/20</span>
              </motion.span>
            ) : (
              <span className="text-[17px] font-extrabold tabular-nums leading-none text-slate-300 motion-safe:animate-pulse">
                –<span className="text-xs font-semibold ml-0.5">/20</span>
              </span>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-5 space-y-5 relative z-10">

        <AnimatePresence>
          {isProcessing && (
            <ProfessorBanner key="prof" corrected={correctedQuestions} total={totalQuestions} statuses={questionStatuses} />
          )}

          {analysisStatus === 'error' && (
            <motion.div
              key="error"
              initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.22, ease: EASE_OUT }}
              className="bg-rose-50 rounded-3xl p-6 border border-rose-100 flex flex-col items-center text-center"
              role="alert"
            >
              <AlertTriangle className="w-10 h-10 text-rose-500 mb-3" />
              <h3 className="font-bold text-slate-900 text-lg mb-2">Impossible de lire la copie</h3>
              <p className="text-slate-600 text-sm mb-5">{currentMessage || 'Vérifie la qualité de la photo et ta connexion.'}</p>
              <button type="button" onClick={() => onBack && onBack()} className="flex items-center gap-2 px-6 min-h-[48px] bg-rose-500 text-white rounded-full font-bold text-sm shadow-md shadow-rose-500/20 active:scale-95 transition-transform touch-manipulation">
                <RefreshCcw className="w-4 h-4" /> Réessayer
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {analysisStatus !== 'error' && (
          <div className="space-y-6">
            {/* Énoncé */}
            {!isPartner && currentData?.exercise_context?.enonce && (
              <motion.div
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.28, ease: EASE_OUT }}
                className="bg-white rounded-3xl border border-slate-200/60 shadow-sm overflow-hidden"
              >
                <div className="px-5 py-3.5 border-b border-slate-100 flex items-center gap-2 bg-slate-50/50">
                  <div className="w-1.5 h-4 bg-indigo-500 rounded-full" />
                  <span className="text-xs font-bold text-slate-700">Contexte de l'exercice</span>
                </div>
                <div className="p-5 text-sm text-slate-700 leading-relaxed">
                  <MathExpression>{currentData.exercise_context.enonce}</MathExpression>
                </div>
              </motion.div>
            )}

            {/* Corrections */}
            <div className="space-y-3">
              <h2 className="text-lg font-bold text-slate-900 px-1">Vos réponses</h2>

              {Object.entries(currentData?.etape_1_student_solutions || {}).map(([questionId, steps], index) => {
                const status = getQuestionStatus(questionId);
                const hasError = status === 'error';
                const isFailed = status === 'failed';
                const isCorrect = status === 'ok';
                const isExpanded = !!expandedQuestions[questionId];

                return (
                  <motion.div
                    key={questionId}
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.26, ease: EASE_OUT, delay: Math.min(index, 8) * 0.04 }}
                    className={`bg-white rounded-3xl border overflow-hidden transition-[border-color,box-shadow] duration-200 ${
                      isExpanded ? 'border-indigo-200 shadow-md shadow-indigo-500/5' : 'border-slate-200/80 shadow-sm'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => toggleQuestion(questionId)}
                      aria-expanded={isExpanded}
                      className="w-full p-4 flex items-center justify-between gap-3 text-left bg-white active:bg-slate-50 transition-colors touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-400"
                    >
                      <span className="flex items-center gap-4 min-w-0">
                        <span className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 transition-colors duration-300 ${
                          hasError ? 'bg-rose-100 text-rose-600'
                            : isFailed ? 'bg-amber-100 text-amber-600'
                              : isCorrect ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'
                        }`}>
                          {/* Le changement de statut « pop » : on voit la correction arriver en direct */}
                          <motion.span
                            key={status}
                            initial={{ scale: 0.4, opacity: 0, rotate: -20 }} animate={{ scale: 1, opacity: 1, rotate: 0 }}
                            transition={{ type: 'spring', stiffness: 460, damping: 18 }}
                            className="flex items-center justify-center"
                          >
                            {hasError ? <XCircle className="w-6 h-6" />
                              : isFailed ? <AlertTriangle className="w-6 h-6" />
                                : isCorrect ? <CheckCircle2 className="w-6 h-6" /> : <Loader2 className="w-5 h-5 animate-spin" />}
                          </motion.span>
                        </span>
                        <span className="min-w-0">
                          <span className="block font-bold text-slate-900 text-[15px]">Question {questionId.replace('Q', '')}</span>
                          <span className={`block text-[13px] font-medium mt-0.5 ${
                            hasError ? 'text-rose-500'
                              : isFailed ? 'text-amber-600'
                                : isCorrect ? 'text-emerald-600' : 'text-slate-500'
                          }`}>
                            {hasError ? `Erreur à l'étape ${convertLineToFrench(getFirstErrorStep(questionId))}`
                              : isFailed ? 'Correction indisponible'
                                : isCorrect ? 'Réponse parfaite'
                                  : <span className="spy-shimmer-text font-semibold">Le professeur corrige…</span>}
                          </span>
                        </span>
                      </span>
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-[transform,background-color,color] duration-200 ${
                        isExpanded ? '-rotate-180 bg-indigo-50 text-indigo-500' : 'bg-slate-50 text-slate-400'
                      }`}>
                        <ChevronDown className="w-5 h-5" />
                      </span>
                    </button>

                    <AnimatePresence initial={false}>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.24, ease: EASE_OUT }}
                          className="bg-slate-50/80 border-t border-slate-100 overflow-hidden"
                        >
                          <div className="p-4 space-y-3">
                            {(() => {
                              let normalizedSteps = steps;
                              if (typeof steps === 'string') {
                                normalizedSteps = {};
                                steps.split('\n').forEach((line, i) => { if (line.trim()) normalizedSteps[`L${i + 1}`] = line.trim(); });
                              } else if (Array.isArray(steps)) {
                                normalizedSteps = {};
                                steps.forEach((line, i) => { if (line && line.trim()) normalizedSteps[`L${i + 1}`] = line.trim(); });
                              }

                              return Object.entries(normalizedSteps || {}).map(([stepId, content]) => {
                                const isRed = shouldStepBeRed(questionId, stepId);
                                const isFirstError = isFirstErrorStep(questionId, stepId);
                                const latexContent = prepareMathContent(content);
                                const explanation = isFirstError ? getFirstErrorExplanation(questionId) : null;

                                return (
                                  <div key={stepId} ref={(el) => { if (el) stepRefs.current[`${questionId}-${stepId}`] = el; }} className="relative flex items-start gap-3">
                                    <div className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold mt-1 shadow-sm border ${
                                      isFirstError ? 'bg-rose-500 text-white border-rose-600'
                                        : isRed ? 'bg-slate-200 text-slate-400 border-slate-300' : 'bg-white text-slate-600 border-slate-200'
                                    }`}>
                                      {stepId.replace('L', '')}
                                    </div>

                                    <div className={`flex-1 min-w-0 p-3 rounded-2xl transition-shadow duration-300 ${
                                      isFirstError
                                        ? `bg-rose-50 ring-1 ${highlightErrors[questionId] ? 'ring-2 ring-rose-400' : 'ring-rose-200/50'}`
                                        : 'bg-white shadow-sm ring-1 ring-slate-900/5'
                                    } ${isRed && !isFirstError ? 'opacity-50' : ''}`}>
                                      <div className="overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                                        <LatexRenderer expression={latexContent} inline={true} className="text-base" />
                                      </div>

                                      {explanation && (
                                        <div className="mt-3 pt-3 border-t border-rose-100">
                                          <div className="flex gap-2 text-[13px] text-rose-900 font-medium leading-relaxed">
                                            <AlertTriangle className="w-4 h-4 text-rose-500 shrink-0 mt-0.5" />
                                            <div className="flex-1 min-w-0">
                                              <Typewriter text={formatExplanation(explanation)} speed={25} delay={800} cursorClass="bg-rose-500" />
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              });
                            })()}

                            <div className="pt-2">
                              <button
                                type="button"
                                onClick={() => toggleSolution(questionId)}
                                aria-expanded={!!showSolution[questionId]}
                                className={`w-full min-h-[48px] rounded-2xl text-[14px] font-bold transition-[transform,background-color] duration-150 active:scale-[0.98] flex items-center justify-center gap-2 touch-manipulation ${
                                  showSolution[questionId]
                                    ? 'bg-slate-200/70 text-slate-700'
                                    : 'bg-slate-900 text-white shadow-md shadow-slate-900/10'
                                }`}
                              >
                                {showSolution[questionId] ? <Eye className="w-4 h-4 opacity-50" /> : <Check className="w-4 h-4" />}
                                {showSolution[questionId] ? 'Masquer' : 'Voir la correction officielle'}
                              </button>

                              <AnimatePresence initial={false}>
                                {showSolution[questionId] && (
                                  <motion.div
                                    initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                                    transition={{ duration: 0.22, ease: EASE_OUT }}
                                    className="mt-3 bg-indigo-950 rounded-2xl overflow-hidden"
                                  >
                                    <div className="p-5 font-mono text-[13px] text-indigo-50 leading-relaxed overflow-x-auto">
                                      <Typewriter text={solutionText[questionId] || ''} speed={15} cursorClass="bg-indigo-400" />
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Toast */}
      <AnimatePresence>{toast && <Toast key={toast.id} toast={toast} />}</AnimatePresence>

      {/* ---- Zone basse : dégradé + « Terminer » à droite (le bouton d'appel est à gauche) ---- */}
      {analysisStatus !== 'error' && (
        <>
          <div className="pointer-events-none fixed inset-x-0 bottom-0 z-30 h-28 bg-gradient-to-t from-slate-50 via-slate-50/80 to-transparent" aria-hidden="true" />

          {showCallButton && !isCalling && (
            <IdleCallWidget onClick={handleStartHelp} disabled={isConnecting} />
          )}

          <motion.button
            type="button"
            onClick={handleGoToChapters}
            whileTap={{ scale: 0.95 }}
            className={`fixed right-3 z-40 h-12 px-5 rounded-full font-bold text-[15px] flex items-center justify-center gap-2 shadow-xl touch-manipulation transition-colors duration-200 ${
              (showCompletionToast || isTimeUp || callStatus === 'completed')
                ? 'bg-emerald-500 text-white shadow-emerald-500/25'
                : 'bg-slate-900 text-white shadow-slate-900/20'
            }`}
            style={{ bottom: DOCK_BOTTOM }}
          >
            <span className="truncate">
              {isTimeUp ? 'Temps écoulé · Valider' : (showCompletionToast || callStatus === 'completed') ? 'Valider le devoir' : 'Terminer'}
            </span>
            <ArrowRight className="w-4 h-4 opacity-80 shrink-0" />
          </motion.button>
        </>
      )}

      {/* ---- Modale de note (temporaire) ---- */}
      <AnimatePresence>
        {showGrade && (
          <GradeModal
            percentage={percentage}
            grade={grade20}
            correct={correctQuestions}
            total={gradeDenominator}
            failed={failedQuestions}
            onClose={closeGrade}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// MotionConfig : respecte automatiquement prefers-reduced-motion
const SmartEx = (props) => (
  <MotionConfig reducedMotion="user">
    <SmartExInner {...props} />
  </MotionConfig>
);

export default SmartEx;